import { useState } from "react";
import { googleSignInAvailable, startGoogleSignIn } from "@/lib/auth";
import styles from "./SsoRow.module.css";

interface SsoRowProps {
  /** Where to return after the callback completes. */
  next: string;
}

/**
 * SSO option row. Renders "Continue with Google" — live when the runtime
 * config has a `cognitoDomain` (the deploy seeded the Google client id +
 * the Hosted UI domain came online), greyed out as "Coming soon" until
 * then.
 *
 * The Microsoft + SAML buttons from the design are intentionally left out
 * — user-approved scope is Google for the first pass.
 */
export function SsoRow({ next }: SsoRowProps) {
  const available = googleSignInAvailable();
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onGoogle() {
    if (!available || pending) return;
    setErr(null);
    setPending(true);
    try {
      await startGoogleSignIn(next);
      // startGoogleSignIn assigns window.location; we won't reach here on
      // success. If the browser blocks the navigation, drop the pending
      // state so the user can retry.
    } catch (e) {
      setPending(false);
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className={styles.row}>
      <button
        type="button"
        className={styles.btn}
        onClick={available ? onGoogle : undefined}
        disabled={!available || pending}
        aria-disabled={!available || pending}
      >
        <GoogleLogo />
        <span className={styles.label}>
          {pending ? "Redirecting to Google…" : "Continue with Google"}
        </span>
        {!available && <span className={styles.tag}>Coming soon</span>}
      </button>
      {err && (
        <p className={styles.err} role="alert">
          {err}
        </p>
      )}
      <div className={styles.divider}>
        <span className={styles.dividerLine} aria-hidden="true" />
        <span className={styles.dividerText}>or with email</span>
        <span className={styles.dividerLine} aria-hidden="true" />
      </div>
    </div>
  );
}

function GoogleLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}
